import {
  BehaviorSubject,
  Observable,
  filter,
  distinctUntilChanged,
} from 'rxjs';

import { IIdentity } from '../models';

class InternalChanges<T extends IIdentity> {
  added = new Map<string | number, T>();
  updated = new Map<string | number, T>();
  deleted = new Map<string | number, T>();
  propertiesChanges: { [key: string]: boolean } = {};
}

export class Changes<T> {
  added: T[] = [];
  updated: T[] = [];
  deleted: T[] = [];
  propertiesChanges: { [key: string]: boolean } = {};
}

/**
 * @description Responsible for tracking array changes including properties of array items.
 */
export class ChangesModel<T extends IIdentity> {
  readonly hasValue$ = new BehaviorSubject<boolean>(false);

  private readonly _isDirty$ = new BehaviorSubject<boolean>(false);
  private changes = new InternalChanges<T>();

  // TODO(Grid) merge changed with propertiesChanged
  // current changes
  get changed(): Changes<T> {
    return {
      added: Array.from(this.changes.added.values()),
      updated: Array.from(this.changes.updated.values()),
      deleted: Array.from(this.changes.deleted.values()),
      propertiesChanges: this.changes.propertiesChanges,
    };
  }

  get dirty(): boolean {
    return this._isDirty$.value;
  }

  get isDirty$(): Observable<boolean> {
    return this._isDirty$.asObservable().pipe(
      distinctUntilChanged(),
      filter((value) => value)
    );
  }

  /**
   * @description Checks if there are any changes registered
   */
  hasValue(): boolean {
    return (
      !!this.changes.added.size ||
      !!this.changes.deleted.size ||
      !!this.changes.updated.size
    );
  }

  /**
   * @description Tracks the given value and properties as being changed
   */
  write(change: { value: T; properties?: (keyof T)[] }): void {
    if (!change) {
      return;
    }

    if (this.changes.added.has(change.value.id)) {
      this.changes.added.set(change.value.id, change.value);
      Object.assign(
        this.changes.propertiesChanges,
        ...this.getPropertiesChanges(change.value)
      );
    } else {
      this.changes.updated.set(change.value.id, change.value);
      Object.assign(
        this.changes.propertiesChanges,
        ...this.getPropertiesChanges(change.value, change.properties)
      );
    }

    this._isDirty$.next(true);
    this.hasValue$.next(true);
  }

  /**
   * @description Tracks the given values as being newly added to the array
   */
  addValues(values: T[], properties?: (keyof T)[]): void {
    if (!values?.length) {
      return;
    }

    values.forEach((value) => {
      this.changes.added.set(value.id, value);
      Object.assign(
        this.changes.propertiesChanges,
        ...this.getPropertiesChanges(value, properties)
      );
    });

    this._isDirty$.next(true);
    this.hasValue$.next(true);
  }

  /**
   * @description Tracks the given values as being deleted.
   * Newly added values are not considered as being deleted but are just discarded from the added changes
   */
  deleteValues(...values: T[]): void {
    if (!values?.length) {
      return;
    }

    values.forEach((value) => {
      if (this.changes.added.has(value.id)) {
        this.changes.added.delete(value.id);
      } else {
        this.changes.updated.delete(value.id);
        this.changes.deleted.set(value.id, value);
      }

      const keys = Object.keys(this.changes.propertiesChanges);
      keys.forEach((property) => {
        if (property.startsWith(this.propertyPrefix(value))) {
          delete this.changes.propertiesChanges[property];
        }
      });
    });

    this._isDirty$.next(true);
    const hasValue =
      !!this.changes.added.size ||
      !!this.changes.updated.size ||
      !!this.changes.deleted.size;
    this.hasValue$.next(hasValue);
  }

  isPropertyChanged(value: T, property?: keyof T): boolean {
    const identifier = this.propertyIdentifier(value, property);

    return identifier in this.changes.propertiesChanges;
  }

  isValueAdded(value: T): boolean {
    return this.changes.added.has(value.id);
  }

  clear(): void {
    this.changes = new InternalChanges();
    this.changes.propertiesChanges = {};
    this.hasValue$.next(false);
    this._isDirty$.next(false);
  }

  setChanges(changes: Changes<T>): void {
    if (!changes) {
      return;
    }

    changes.added?.forEach((x) => {
      this.changes.added.set(x.id, x);
    });
    changes.updated?.forEach((x) => {
      this.changes.updated.set(x.id, x);
    });
    changes.deleted?.forEach((x) => {
      this.changes.deleted.set(x.id, x);
    });

    Object.assign(
      this.changes.propertiesChanges,
      changes.propertiesChanges ?? {}
    );
  }

  removeChanges(changes: string[]): void {
    for (const key of changes) {
      if (this.changes.propertiesChanges[key]) {
        delete this.changes.propertiesChanges[key];
      }
    }
  }

  //#region Private methods

  private getPropertiesChanges(
    value: T,
    properties?: (keyof T)[]
  ): { [key: string]: boolean }[] {
    if (properties?.length) {
      return properties?.map((property) => ({
        [this.propertyIdentifier(value, property)]: true,
      }));
    } else {
      // if no property is specified then we assume all properties were changed
      return [{ [this.propertyIdentifier(value)]: true }];
    }
  }

  private propertyIdentifier(data: T, property?: keyof T): string {
    return `${this.propertyPrefix(data)}${String(property) ?? 'ALL_PROPS'}`;
  }

  private propertyPrefix(data: T): string {
    return `${data.id}__`;
  }

  //#endregion
}
